<template>
  <AmToast
    v-if="toastFeedback"
    :message="toastMessage"
    :duration="3000"
    margin="mt-28 ml-4"
    position="top-right"
    closable
  />

  <div
    class="my-apps"
    :class="{
      'my-apps--mobile': mq.current === 'mobile',
      'my-apps--desktop': mq.current !== 'mobile'
    }"
  >
    <AmLoading
      v-if="loading"
      :isLoading="loading"
      class="m-auto"
    />
    <div
      v-else
      class="my-apps__content"
    >
      <AmEmptyBackground
        v-if="apps.length === 0"
        icon="folder"
        :text="$t('myApps.emptyState.title')"
        :action="$t('myApps.emptyState.action')"
        sizeAction="xlarge"
        @clickAction="router.push({ name: 'create-app' })"
      />
      <DataTable
        v-else
        class="my-apps__table"
        :value="apps"
        dataKey="id"
        rowHover
        stripedRows
        responsiveLayout="stack"
        breakpoint="767px"
        @row-click="onRowClick"
      >
        <Column
          field="name"
          :header="$t('myApps.header.table.name')"
          :style="{ width: '35%' }"
          bodyClass="my-apps__cell my-apps__cell--name"
        >
          <template #body="{ data }">
            <span class="my-apps__cell-label">{{ $t('myApps.header.table.name') }}</span>
            <span
              class="my-apps__row-link"
              :data-test="`row-name-${data.id}`"
            >
              {{ data.name }}
            </span>
          </template>
        </Column>

        <Column
          field="created_at"
          :header="$t('myApps.header.table.createdAt')"
          bodyClass="my-apps__cell"
        >
          <template #body="{ data }">
            <span class="my-apps__cell-label">{{ $t('myApps.header.table.createdAt') }}</span>
            <AmTypography
              :text="date(data.created_at, 'fullDate')"
              typographyStyle="l6"
              textColor="text60"
            />
          </template>
        </Column>

        <Column
          field="is_private"
          :header="$t('myApps.header.table.isPrivate')"
          bodyClass="my-apps__cell"
        >
          <template #body="{ data }">
            <span class="my-apps__cell-label">{{ $t('myApps.header.table.isPrivate') }}</span>
            <AmButton
              v-if="data.is_private"
              :label="$t('myApps.actions.share')"
              variant="maxGhost"
              :icon="{
                name: 'share',
                size: 'small',
                position: 'withLeftIcon'
              }"
              @click.stop="copyURL(data.access_url)"
            />
            <AmTypography
              v-else
              text="-"
              typographyStyle="l6"
              textColor="text120"
            />
          </template>
        </Column>

        <Column
          field="status_name"
          bodyClass="my-apps__cell"
        >
          <template #header>
            <span class="flex items-center gap-1">
              {{ $t('myApps.header.table.status') }}
              <button
                type="button"
                class="my-apps__status-helper"
                aria-label="Ver status disponíveis"
                @click.stop="openStatusModal"
              >
                <span class="material-icons text-base">help_outline</span>
              </button>
            </span>
          </template>
          <template #body="{ data }">
            <span class="my-apps__cell-label">{{ $t('myApps.header.table.status') }}</span>
            <AmChip
              :text="status(data.status_name)?.tag ? $t(`myApps.status.${status(data.status_name).tag}`) : data.status_name"
              :data-test="`status-tag-${status(data.status_name)?.tag || 'unknown'}`"
              :type="status(data.status_name)?.type || 'secondary'"
              isStatusTag
            />
          </template>
        </Column>

        <Column
          :header="$t('myApps.header.table.actions')"
          :style="{ width: '80px' }"
          bodyClass="my-apps__cell my-apps__actions-cell"
        >
          <template #body="{ data }">
            <span class="my-apps__cell-label">{{ $t('myApps.header.table.actions') }}</span>
            <div @click.stop>
              <!-- Configurando (in-draft + development unificados) -->
              <AmDropdown
                v-if="status(data.status_name)?.tag === 'configuring'"
                v-model="action"
                iconController
                absolute
                size="fitContent"
                location="bottom end"
                dataTestName="actions-dropdown-my-apps-configuring"
                :options="[
                  {
                    title: $t('myApps.actions.configuring'),
                    value: `configuring ${data.id}`,
                    action: () => handleAction('configuring', data.id, data)
                  }
                ]"
              />

              <!-- Em teste -->
              <AmDropdown
                v-else-if="status(data.status_name)?.tag === 'inTest'"
                v-model="action"
                iconController
                absolute
                size="fitContent"
                location="bottom end"
                dataTestName="actions-dropdown-my-apps-intest"
                :options="[
                  {
                    title: $t('myApps.actions.development'),
                    value: `development ${data.id}`,
                    action: () => handleAction('development', data.id, data)
                  },
                  {
                    title: $t('myApps.actions.credentials'),
                    value: `credentials ${data.id}`,
                    action: () => handleAction('credentials', data.id, data)
                  }
                ]"
              />

              <!-- Em análise -->
              <AmDropdown
                v-else-if="status(data.status_name)?.tag === 'inAnalysis'"
                v-model="action"
                iconController
                absolute
                size="fitContent"
                location="bottom end"
                dataTestName="actions-dropdown-my-apps-inanalysis"
                :options="[
                  {
                    title: $t('myApps.actions.credentials'),
                    value: `credentials ${data.id}`,
                    action: () => handleAction('credentials', data.id, data)
                  }
                ]"
              />

              <!-- Publicado -->
              <AmDropdown
                v-else-if="status(data.status_name)?.tag === 'published'"
                v-model="action"
                iconController
                absolute
                size="fitContent"
                location="bottom end"
                dataTestName="actions-dropdown-my-apps-published"
                :options="[
                  {
                    title: $t('myApps.actions.edit'),
                    value: `edit ${data.id}`,
                    action: () => handleAction('edit', data.id)
                  },
                  {
                    title: $t('myApps.actions.deactivate'),
                    value: `deactivate ${data.id}`,
                    action: () => handleAction('deactivate', data.id)
                  },
                  {
                    title: $t('myApps.actions.credentials'),
                    value: `credentials ${data.id}`,
                    action: () => handleAction('credentials', data.id, data)
                  }
                ]"
              />

              <!-- Em homologação -->
              <AmDropdown
                v-else-if="status(data.status_name)?.tag === 'homolog'"
                v-model="action"
                iconController
                absolute
                size="fitContent"
                location="bottom end"
                dataTestName="actions-dropdown-my-apps-homolog"
                :options="[
                  {
                    title: $t('myApps.actions.credentials'),
                    value: `credentials ${data.id}`,
                    action: () => handleAction('credentials', data.id, data)
                  }
                ]"
              />

              <!-- Ajuste pendente -->
              <AmDropdown
                v-else-if="status(data.status_name)?.tag === 'adjustmentPending'"
                v-model="action"
                iconController
                absolute
                size="fitContent"
                location="bottom end"
                dataTestName="actions-dropdown-my-apps-adjustment"
                :options="[
                  {
                    title: $t('myApps.actions.adjustment'),
                    value: `adjustment ${data.id}`,
                    action: () => handleAction('adjustment', data.id)
                  }
                ]"
              />

              <!-- Reprovado -->
              <AmDropdown
                v-else-if="status(data.status_name)?.tag === 'reproved'"
                v-model="action"
                iconController
                absolute
                size="fitContent"
                location="bottom end"
                dataTestName="actions-dropdown-my-apps-reproved"
                :options="[
                  {
                    title: $t('myApps.actions.deactivate'),
                    value: `deactivate ${data.id}`,
                    action: () => handleAction('deactivate', data.id)
                  }
                ]"
              />

              <!-- Desativado -->
              <AmDropdown
                v-else-if="status(data.status_name)?.tag === 'deactivate'"
                v-model="action"
                iconController
                absolute
                size="fitContent"
                location="bottom end"
                dataTestName="actions-dropdown-my-apps-deactivate"
                :options="[
                  {
                    title: $t('myApps.actions.reactivate'),
                    value: `reactivate ${data.id}`,
                    action: () => handleAction('reactivate', data.id)
                  }
                ]"
              />
            </div>
          </template>
        </Column>
      </DataTable>
    </div>
  </div>

  <AmModal
    v-model="showModal"
    maxWidth="500px"
    :title="$t(`amDeactivateAppModal.title`)"
    class="app-config-finished-modal"
    :persistent="true"
    @close="showModal = false"
  >
    <div :class="[`deactivate-app-${mq.current}`]">
      <AmTypography
        :text="$t(`amDeactivateAppModal.content`)"
        enfaseTextColor="black"
        enfaseText="desenvolvedores@appmax.com.br "
        :nextText="$t('amDeactivateAppModal.nextContent')"
        class="home-view__left__infos__subtitle--label"
      />
      <AmButton
        :label="$t('amDeactivateAppModal.understand')"
        variant="maxPrimary"
        class="float-right app-config-finished-modal__button"
        @click="showModal = false"
      />
    </div>
  </AmModal>

  <AmModal
    v-model="showModalReactivate"
    :title="$t(`amReactivateAppModal.title`)"
    maxWidth="500px"
    :persistent="true"
    @close="closeModalReactivate"
  >
    <AmReactivateAppModal @understandModalReactivate="showModalReactivate = false" />
  </AmModal>

  <AmModal
    v-model="showStatusModal"
    title="Status"
    maxWidth="800px"
    :persistent="true"
    @close="closeStatusModal"
  >
    <AmStatusModal />
  </AmModal>

  <AmModal
    v-model="showModalCredentials"
    maxWidth="500px"
    :title="$t('myApps.credentialsModal.title')"
    :persistent="true"
    @close="closeCredentialsModal"
  >
    <AmLoading
      v-if="loadingCredentials"
      :isLoading="loadingCredentials"
      class="mx-auto pb-10"
    />
    <div
      v-else
      :class="[`app-config-finished-modal-${mq.current}`]"
    >
      <div class="app-config-finished-modal">
        <div
          v-if="appCredentials.error"
          class="mb-4 p-3 bg-red-100 text-red-700 rounded"
        >
          <strong>Erro:</strong> {{ appCredentials.error }}
        </div>

        <div
          v-else-if="hasValidCredentials"
          class="app-config-finished-modal__top-content"
        >
          <AmTypography
            :text="$t('myApps.credentialsModal.appId')"
            typographyStyle="l5"
            weight="medium"
            class="mt-4"
          />

          <AmInput
            v-if="appCredentials.appId"
            :modelValue="appCredentials.appId"
            inputCopy
            readOnly
            size="fullWidth"
            class="mb-3"
          />

          <div
            v-if="appCredentials.sandbox && (appCredentials.sandbox.client_id || appCredentials.sandbox.client_secret)"
            class="credential-divider"
          >
            <AmTypography
              :text="$t('myApps.credentialsModal.sandbox')"
              typographyStyle="l6"
              weight="bold"
              textColor="700"
              class="mb-3"
            />

            <AmTypography
              :text="$t('myApps.credentialsModal.sandboxDescription')"
              typographyStyle="l7"
              class="mb-3"
            />

            <AmTypography
              :text="$t('myApps.credentialsModal.clientId')"
              typographyStyle="l6"
              weight="medium"
            />

            <AmInput
              v-if="appCredentials.sandbox.client_id"
              :modelValue="appCredentials.sandbox.client_id"
              inputCopy
              readOnly
              size="fullWidth"
              class="mb-4"
            />

            <AmTypography
              :text="$t('myApps.credentialsModal.clientSecret')"
              typographyStyle="l6"
              weight="medium"
            />

            <AmInput
              v-if="appCredentials.sandbox.client_secret"
              :modelValue="appCredentials.sandbox.client_secret"
              inputCopy
              readOnly
              size="fullWidth"
              class="mb-2"
            />
          </div>

          <div
            v-if="
              !appCredentials.showOnlySandbox &&
                appCredentials.homolog &&
                (appCredentials.homolog.client_id || appCredentials.homolog.client_secret)
            "
          >
            <AmTypography
              :text="$t('myApps.credentialsModal.production')"
              typographyStyle="l6"
              weight="bold"
              class="mb-3"
            />

            <AmTypography
              :text="$t('myApps.credentialsModal.productionDescription')"
              typographyStyle="l7"
              class="mb-4"
            />

            <AmTypography
              :text="$t('myApps.credentialsModal.clientId')"
              typographyStyle="l6"
              weight="medium"
            />

            <AmInput
              v-if="appCredentials.homolog.client_id"
              :modelValue="appCredentials.homolog.client_id"
              inputCopy
              readOnly
              size="fullWidth"
              class="mb-4"
            />

            <AmTypography
              :text="$t('myApps.credentialsModal.clientSecret')"
              typographyStyle="l6"
              weight="medium"
            />

            <AmInput
              v-if="appCredentials.homolog.client_secret"
              :modelValue="appCredentials.homolog.client_secret"
              inputCopy
              readOnly
              size="fullWidth"
              class="mb-8"
            />
          </div>
        </div>

        <div
          v-else-if="!appCredentials.error"
          class="mb-4 p-3 bg-yellow-100 text-yellow-700 rounded"
        >
          <strong>Aviso:</strong> Nenhuma credencial encontrada para este aplicativo.
        </div>

        <div class="app-config-finished-modal__bottom-content__button mt-6 flex justify-end">
          <AmButton
            :label="$t('myApps.credentialsModal.action')"
            variant="maxPrimary"
            @click="closeCredentialsModal"
          />
        </div>
      </div>
    </div>
  </AmModal>
</template>

<script setup>
import DataTable from '@appmax_npm/ds-prime/datatable'
import Column from '@appmax_npm/ds-prime/column'
import {
  AmButton,
  AmLoading,
  AmChip,
  AmTypography,
  AmDropdown,
  AmModal,
  AmEmptyBackground,
  AmToast,
  AmInput
} from '@appmax_npm/ds-appmax-v3'
import { computed, inject, onMounted, ref } from 'vue'
import './MyApps.scss'
import AmStatusModal from '../../molecules/AmStatusModal/AmStatusModal.vue'
import AmReactivateAppModal from '../../molecules/AmReactivateAppModal/AmReactivateAppModal.vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useHttpStore } from '../../../stores/http'
import { useAppStore, ScreenMode } from '@/stores/modules/app'
import { getStatusBadgeProps, normalizeStatusKey } from '@/utils/appStatus'

const mq = inject('mq')
const loading = ref(false)
const router = useRouter()
const httpStore = useHttpStore()
const appStore = useAppStore()
const { date } = inject('formatter')
const { t } = useI18n()
const env = inject('env')

const apps = ref([])
const toastFeedback = ref(false)
const toastMessage = ref(null)
const action = ref('')
const showModal = ref(false)
const showModalReactivate = ref(false)
const showModalCredentials = ref(false)
const showStatusModal = ref(false)
// AppDeveloperModal removido: acao 'development' navega para /apps/:id (AppDetailsView)
const appCredentials = ref({})
const loadingCredentials = ref(false)

const hasValidCredentials = computed(() => {
  const sandboxValid = appCredentials.value.sandbox && (appCredentials.value.sandbox.client_id || appCredentials.value.sandbox.client_secret)

  const homologValid =
    !appCredentials.value.showOnlySandbox &&
    appCredentials.value.homolog &&
    (appCredentials.value.homolog.client_id || appCredentials.value.homolog.client_secret)

  const hasAppId = appCredentials.value.appId

  return (sandboxValid || homologValid) && hasAppId
})

const closeStatusModal = () => {
  showStatusModal.value = false
}

const closeModalReactivate = () => {
  showModalReactivate.value = false
}

const closeCredentialsModal = () => {
  showModalCredentials.value = false
  appCredentials.value = {}
}


const onRowClick = ({ data, originalEvent }) => {
  if (originalEvent?.target?.closest('button, a, input, [role="button"], .am-dropdown')) {
    return
  }
  router.push({ name: 'app', params: { id: data.id.toString() } })
}

const copyURL = (url) => {
  navigator.clipboard.writeText(`${env.app.systemURL}/${url}`)

  toastMessage.value = {
    alertType: 'success',
    title: t('myApps.toast.copyURL')
  }

  toastFeedback.value = true

  setTimeout(() => {
    toastFeedback.value = false
  }, 3000)
}

// Mapa de ações por tag canônico — determina qual CTA aparece no dropdown/link
const ACTION_MAP = {
  configuring:        { label: 'configuring' },
  inTest:             { label: 'development' },
  inAnalysis:         { label: 'edit' },
  adjustmentPending:  { label: 'adjustment' },
  homolog:            { label: 'credentials' },
  published:          null,
  reproved:           { label: 'deactivate' },
  deactivate:         { label: 'reactivate' }
}

/**
 * Retorna as props do badge + ação para um statusName vindo do backend.
 * Usa getStatusBadgeProps() do utils centralizado como fonte única.
 */
const status = (statusName) => {
  const props = getStatusBadgeProps(statusName)

  if (!props) {
    return { tag: 'unknown', type: 'secondary', action: null }
  }

  return {
    ...props,
    action: ACTION_MAP[props.tag] ?? null
  }
}

const handleAction = (actionLabel, id, item = []) => {
  return {
    edit: () => router.push({ path: `/app/${id}/edit` }),
    // 'development' mantido para compatibilidade (inTest usa este label)
    development: () => {
      router.push({ name: 'app', params: { id: id.toString() } })
    },
    // 'configuring' — ação unificada para in-draft e development
    configuring: () => {
      if (item?.status_name && normalizeStatusKey(item.status_name) === 'in-draft') {
        router.push({ name: 'create-app' })
        appStore.screenMode = ScreenMode.DRAFT
        appStore.isSelectedApp = true
        appStore.app.id = id
      } else {
        router.push({ name: 'app', params: { id: id.toString() } })
      }
    },
    deactivate: () => {
      deactivateAppModal()
    },
    reactivate: () => {
      reactivateAppModal()
    },
    credentials: () => {
      getAppCredentials(id, item)
    },
    inDraft: () => {
      router.push({ name: 'create-app' })
      appStore.screenMode = ScreenMode.DRAFT
      appStore.isSelectedApp = true
      appStore.app.id = id
    },
    adjustment: () => {
      router.push({ path: `/app/${id}/edit` })
    }
  }[actionLabel]()
}

const openStatusModal = () => {
  showStatusModal.value = true
}

const getApps = async () => {
  loading.value = true

  httpStore.getApps().then((response) => {
    const {
      data: { data }
    } = response

    apps.value = data || []

    loading.value = false
  })
}


const deactivateAppModal = () => {
  if (!showModal.value) {
    showModal.value = !showModal.value
  } else {
    showModal.value = !showModal.value
    setTimeout(() => {
      showModal.value = !showModal.value
    }, 100)
  }
}

const reactivateAppModal = () => {
  if (!showModalReactivate.value) {
    showModalReactivate.value = !showModalReactivate.value
  } else {
    showModalReactivate.value = !showModalReactivate.value
    setTimeout(() => {
      showModalReactivate.value = !showModalReactivate.value
    }, 100)
  }
}

const getAppCredentials = async (appId, appItem = null) => {
  loadingCredentials.value = true

  try {
    const response = await httpStore.getAppCredentials(appId)

    // Extrair os dados das credenciais corretamente
    const credentialsData = response?.data?.data?.stages || {}

    // Buscar o UUID do aplicativo na lista atual ou na resposta da API
    const currentApp = apps.value.find((app) => app.id === appId) || appItem
    const appUuid = currentApp?.uuid || response?.data?.data?.uuid || response?.data?.data?.app_uuid || response?.data?.data?.id || appId

    // Verificar o status do aplicativo para determinar quais credenciais mostrar
    const appStatusProps = status(currentApp?.status_name)
    const shouldShowOnlySandbox = appStatusProps?.tag === 'inAnalysis' || appStatusProps?.tag === 'inTest'

    // Backend retorna stages.homolog (dev/sandbox) e stages.production (real).
    // Mapeamos para os nomes usados na UI: sandbox e homolog (homolog = producao).
    appCredentials.value = {
      appId: appUuid,
      sandbox: credentialsData.homolog || {},
      homolog: shouldShowOnlySandbox ? {} : credentialsData.production || {},
      showOnlySandbox: shouldShowOnlySandbox
    }

    showModalCredentials.value = true
  } catch (error) {
    appCredentials.value = {
      error: error.message,
      errorDetails: error
    }
    showModalCredentials.value = true
  } finally {
    loadingCredentials.value = false
  }
}

onMounted(async () => {
  loading.value = true

  await getApps()

  // Fase 1.2: toast informativo após configuração concluída
  if (appStore.justConfiguredAppId) {
    toastMessage.value = {
      alertType: 'info',
      title: t('myApps.toast.configuredSummary'),
      description: t('myApps.toast.configuredDetail')
    }
    toastFeedback.value = true
    setTimeout(() => {
      toastFeedback.value = false
    }, 5000)
    appStore.justConfiguredAppId = null
  }
})
</script>
